import { queryArgs } from '@/utils';
import routes from './routes';

/**
 * Helper to get a route for a given slug.
 *
 * @param {string} slug The slug to find a route for.
 *
 * @returns {object|boolean} The route object or false if no route found.
 */
function getRouteForSlug(slug) {
	slug = slug.replace('optin-monster-', '');

	if (slug) {
		return routes.find((r) => {
			if (r.meta.slug.includes('=:')) {
				return slug === (r.name || r.meta.slug);
			}

			return slug === (r.meta.slug || r.name);
		});
	}

	return false;
}

/**
 * Helper to get a route for a given url.
 *
 * @param {string} url The url to parse for a route.
 *
 * @returns {null|object} The route path or false if no route found.
 */
function getRouteForUrl(url) {
	const pattern = /[?&]page=(optin-monster-[^&#\t\n\r]+)+/;
	const matches = url.match(new RegExp(pattern));

	return matches && matches[1] && getRouteForSlug(matches[1]);
}

/**
 * Helper to get a route path for a given query string.
 *
 * @param {object} route The route object.
 * @param {object} route.route The route object.
 * @param {object} route.args The query args to pull params from.
 *
 * @returns {object} The route path or false if no route found.
 */
function getRoutePathFromArgs({ route, args }) {
	const pathArgs = queryArgs.parse(route.path);
	let params = {};

	Object.keys(pathArgs).forEach((pathArg) => {
		if (args[pathArg]) {
			params[pathArg] = args[pathArg];
		}
	});

	return { name: route.name, params };
}

/**
 * Helper to get a route for a given query string.
 *
 * @param {object} args The query args to find a route for.
 *
 * @returns {object|boolean} The route path or false if no route found.
 */
function getRouteForArgs(args) {
	const slug = args.page.replace('optin-monster-', '');
	delete args.page;

	const findMatches = (r) => {
		const name = r.meta.slug || r.name || '';

		return name.match(slug);
	};

	const addPriority = (route) => {
		const keys = Object.keys(args).filter((arg) => route.path.match(arg));

		let matchedArgs = {};
		keys.forEach((k) => {
			matchedArgs[k] = args[k];
		});

		return {
			args: matchedArgs,
			priority: keys.length,
			route,
		};
	};

	const sortByPriority = (a, b) => b.priority - a.priority;

	const matchingRoute = routes.filter(findMatches).map(addPriority).sort(sortByPriority).shift();

	if (matchingRoute) {
		return getRoutePathFromArgs(matchingRoute);
	}

	return false;
}

/**
 * Helper to get a route for a given query string.
 *
 * @param {null|string} queryString The query string to parse for a route.
 *
 * @returns {object|boolean} The route path or false if no route found.
 */
function getRouteForQuery(queryString = null) {
	const args = queryArgs.parse(queryString);
	const { page } = args;

	if (!page || !page.match('optin-monster-')) {
		return false;
	}

	if (1 < Object.keys(args).length) {
		const route = getRouteForArgs(args);
		if (route) {
			return route;
		}
	}

	return getRouteForSlug(page);
}

/**
 * Helper to get the previous route.
 *
 * @returns {object} The previous route.
 */
function getPreviousRoute() {
	return this._prevRoute;
}

/**
 * Helper to go to the previous route.
 *
 * @returns {void}
 */
function goPrevious() {
	const prev = this.getPreviousRoute();
	this.push(prev.name ? prev : { name: 'dashboard' });
}

export default {
	getRouteForSlug,
	getRouteForUrl,
	getRouteForQuery,
	getPreviousRoute,
	goPrevious,
};
